import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import ParameterFieldBase, { IParameterFieldBaseOptions } from '../../../_common/platform/ts/ParameterFieldBase';
import * as DateTimeFunctions from '../../../_common/platform/ts/DateTimeFunctions';
import { IValuesCollection } from '../../../_common/platform/ts/appTheme';
import { themeVars } from '../../../pdc-platform/src/ts/themes/themeConstants';
import { ATTRIBUTE_TYPES } from '../../../_common/platform/ts/ConfigurationConstants';
import { IAttributeName } from '../../platform/tsx/filtering/Interfaces';
import { isTimeIntervalValueFormat } from './formatChooser/FormatChooserValueSelector';
import { parseBooleanCustomFormat } from '../../../_common/platform/ts/Utilities';

import {
    FormatChooserPopup,
    getDefaultAttributeType,
    IFormatChooserPopupOptions
} from './formatChooser/FormatChooserPopup';

import { IAttributeTypeGetter, IDefaultFormatGetter, IFormatChooserValue } from './formatChooser/Interfaces';

export { IFormatChooserValue } from './formatChooser/Interfaces';

require('../../../_common/platform/sass/format-chooser.sass');

//LL: note: .type (=> interface IValueForGauge) is used not only in Gauge. Ex, in Charts FormatConfigField
interface IValueForGauge extends IFormatChooserValue {
    type?: string;
}

export interface IFormatChooserOptions {
    value: any;
    theme: IValuesCollection;
    themeVars?: IFormatThemeVars;
    popupTitle?: string;
    isNoValue?: boolean;
    isScalar?: boolean;
    customizeFormat?: boolean;
    showStyle?: boolean;
    showTemplate?: boolean;
    showRawTemplate?: boolean;
}

export interface IFormatThemeVars {
    foregroundColor?: string;
    backgoundColor?: string;
    fontFace?: string;
    fontSize?: string;
    fontWeight?: string;
    fontItalic?: string;
}

export interface IFormatChooserValues {
    values?;
    gaugeFromToField?;
}

export interface IFormatChooserProperties {
    isScalar: boolean;
    options?: IFormatChooserOptions;
    //VR: it is set of all properties from Gauge Config dialog;
    values?: IFormatChooserValues;
    value: IFormatChooserValue;
    attributeType?: string;
    theme: IValuesCollection;
    dataSourceID: number;
    customizeFormat: boolean;
    showAlignmentSelector?: boolean;
    showStyle: boolean;
    showTemplate: boolean;
    showRawTemplate?: boolean;
    popupTitle?: string;
    activeTab?: number;
    glob: IGlob;

    getDefaultAttributeType?: IAttributeTypeGetter; // Custom getter for a widget like Cards.
    getDefaultFormat?: IDefaultFormatGetter;

    onChange?(value: IFormatChooserValue): void;
}

function isExpression(value: IAttributeName) {
    return value != null && value.attribute == null;
}

function normalizeValue(value: IFormatChooserValue, theme: IValuesCollection) {
    value = $.extend(true, {}, value);

    if (value.precision == null) {
        delete value.precision;
    }

    if (value.formatFront == null || value.formatFront === 'none' || value.formatFront === 'default') {
        delete value.formatFront;
    }

    if (value.currency == null || value.formatFront == null || value.formatFront.indexOf('currency') === -1) {
        delete value.currency;
    }

    if (value.customFormat == null || value.customFormat === '') {
        delete value.customFormat;
    }

    if (!value.overwriteInheritedUseDigitGrouping) {
        delete value.useDigitGrouping;
    }

    if (!value.overwriteInheritedFormatZeroAsBlank) {
        delete value.formatZeroAsBlank;
    }

    if (value.useFiscalPrefix !== false) {
        delete value.useFiscalPrefix;
    }

    if (value.formatAsType != null) {
        if (value.expression?.rettype !== ATTRIBUTE_TYPES.ANYTYPE) {
            delete value.formatAsType;
        }
    }

    const unescapedTemplate = _.unescape(value.template).trim();

    if (unescapedTemplate.length == 0) {
        delete value.template;
    } else {
        value.template = unescapedTemplate;
    }

    if (theme != null) {
        if (value.cellStyle != null) {
            const themesCS = theme[themeVars.color_scales_set];
            const themesDM = theme[themeVars.discreteMapping_set];
            const attributeIsExpression = isExpression(value);

            //  - back
            if (value.cellStyle.background_mappingType === 'ColorScale') {
                const existUsedTheme = _.find(themesCS, (obj) => {
                    return obj.name == value.cellStyle.background_mappingName;
                });

                if (!existUsedTheme) {
                    delete value.cellStyle.background_mappingName;
                    delete value.cellStyle.background_mappingType;
                    delete value.cellStyle.backgroundColorSchema; //  for OLD compatibility
                }
            } else if (value.cellStyle.background_mappingType === 'DiscreteMapping') {
                const existUsedTheme = _.find(themesDM, (obj) => {
                    return obj.name == value.cellStyle.background_mappingName;
                });

                if (!existUsedTheme) {
                    delete value.cellStyle.background_mappingName;
                    delete value.cellStyle.background_mappingType;
                    delete value.cellStyle.backgroundColorSchema; //  for OLD compatibility
                }
            } else if (value.cellStyle.background_mappingType === 'AttributeMapping') {
                if (attributeIsExpression) {
                    delete value.cellStyle.background_mappingName;
                    delete value.cellStyle.background_mappingType;
                    delete value.cellStyle.backgroundColorSchema; //  for OLD compatibility
                }
            } else if (value.cellStyle.background_mappingName === 'none') {
                delete value.cellStyle.background_mappingName;
                delete value.cellStyle.background_mappingType;
                delete value.cellStyle.backgroundColorSchema; //  for OLD compatibility
            }

            //  - fore
            if (value.cellStyle.foreground_mappingType === 'ColorScale') {
                const existUsedTheme = _.find(themesCS, (obj) => {
                    return obj.name == value.cellStyle.foreground_mappingName;
                });

                if (!existUsedTheme) {
                    delete value.cellStyle.foreground_mappingName;
                    delete value.cellStyle.foreground_mappingType;
                    delete value.cellStyle.foregroundColorSchema; //  for OLD compatibility
                }
            } else if (value.cellStyle.foreground_mappingType === 'DiscreteMapping') {
                const existUsedTheme = _.find(themesDM, (obj) => {
                    return obj.name == value.cellStyle.foreground_mappingName;
                });

                if (!existUsedTheme) {
                    delete value.cellStyle.foreground_mappingName;
                    delete value.cellStyle.foreground_mappingType;
                    delete value.cellStyle.foregroundColorSchema; //  for OLD compatibility
                }
            } else if (value.cellStyle.foreground_mappingType === 'AttributeMapping') {
                if (attributeIsExpression) {
                    delete value.cellStyle.foreground_mappingName;
                    delete value.cellStyle.foreground_mappingType;
                    delete value.cellStyle.foregroundColorSchema; //  for OLD compatibility
                }
            } else if (value.cellStyle.foreground_mappingName === 'none') {
                delete value.cellStyle.foreground_mappingName;
                delete value.cellStyle.foreground_mappingType;
                delete value.cellStyle.foregroundColorSchema; //  for OLD compatibility
            }
        }
    }

    return value;
}

function cleanValueUp(value: IFormatChooserValue) {
    value = { ...value };

    const cellStyle = value.cellStyle;

    if (cellStyle != null) {
        if (cellStyle.alignment === null || (cellStyle.alignment as string) === 'default') {
            delete cellStyle.alignment;
        }

        if (cellStyle.backgroundColor == null) {
            delete cellStyle.backgroundColor;
        }

        if (cellStyle.foregroundColor == null) {
            delete cellStyle.foregroundColor;
        }

        if (cellStyle.fontFace == null) {
            delete cellStyle.fontFace;
        }

        if (cellStyle.fontSize == null) {
            delete cellStyle.fontSize;
        }

        if (cellStyle.fontWeight == null) {
            delete cellStyle.fontWeight;
        }

        if (cellStyle.fontItalic == null) {
            delete cellStyle.fontItalic;
        }

        if (_.isEmpty(cellStyle)) {
            delete value.cellStyle;
        }
    }

    if (value.fiscalYearDivider == null) {
        delete value.fiscalYearDivider;
    }

    return value;
}

function getValueforGauge(values: IFormatChooserValues, value: IFormatChooserValue, options: IFormatChooserOptions) {
    let result: IValueForGauge = {
        aggregateGroup: 'sum',
        type: ATTRIBUTE_TYPES.REAL,
        intervals: null,
        precision: 2
    };

    if (values != null && values.values != null && values.gaugeFromToField != null) {
        let controlValues = values.values;
        let controlFromToField = values.gaugeFromToField;

        if (controlValues[0] != undefined && controlValues[0].attribute != undefined) {
            result = {
                aggregateGroup: controlValues[0].attribute.aggregateGroup || 'sum',
                type: controlValues[0].attribute.attrType || ATTRIBUTE_TYPES.REAL,
                intervals: null
            };
        } else if (controlFromToField.endValueExplicit === false) {
            result = {
                aggregateGroup: controlFromToField.endValueLookup.aggregateGroup || 'sum',
                type: controlFromToField.endValueLookup.attrType || ATTRIBUTE_TYPES.REAL,
                intervals: null
            };
        }
    }

    if (options.value != undefined && !_.isEmpty(options.value)) {
        const fieldName = options.value['fieldName'];

        if (fieldName != null && values != null) {
            const fieldParams = values[fieldName] || {};

            result = _.extend(result, {
                formatFront: fieldParams.formatFront,
                selectedFormat: fieldParams.formatFront,
                precision: fieldParams.precision,
                currency: fieldParams.currency
            });
        }
    }

    return _.extend(result, {
        formatFront: value.formatFront,
        selectedFormat: value.formatFront,
        precision: value.precision,
        currency: value.currency
    });
}

export function FormatChooser(props: IFormatChooserProperties) {
    const calcValue = React.useRef<IValueForGauge>({}); // VR: for Gauge

    const { options } = props;
    const theme = props.theme ?? options?.theme;
    const value = normalizeValue(props.value, theme);
    const isForGauge = options != null && options.isNoValue === true;

    const handleOnChange = (value: IFormatChooserValue) => {
        if (typeof props.onChange === 'function') {
            const valueCopy = normalizeValue(value, props.theme);
            const cleanedValue = cleanValueUp(valueCopy);

            props.onChange(cleanedValue);
        }
    };

    const handleOnIconClick = (e: React.MouseEvent<HTMLSpanElement>) => {
        const popupOptions: IFormatChooserPopupOptions = {            
            editorConfig: { resize_enabled: false, allowedContent: true, disallowedContent: false },
            offset: $(e.target).offset(),
            theme: theme,
            dataSourceID: props.dataSourceID,
            attributeType: props.attributeType,
            popupTitle: props.popupTitle != null ? props.popupTitle : options != null ? options.popupTitle : undefined,
            showAlignmentSelector: props.showAlignmentSelector != null ? props.showAlignmentSelector : false,
            activeTab: props.activeTab,
            glob: props.glob,

            getDefaultAttributeType: props.getDefaultAttributeType,
            getDefaultFormat: props.getDefaultFormat,
            onChange: handleOnChange
        };

        popupOptions.isScalar = props.isScalar;
        popupOptions.customizeFormat = props.customizeFormat;
        popupOptions.showStyle = props.showStyle;
        popupOptions.showTemplate = props.showTemplate;
        popupOptions.showRawTemplate = props.showRawTemplate;

        if (options != null) {
            if (popupOptions.isScalar == null) {
                popupOptions.isScalar = options.isScalar;
            }

            if (popupOptions.customizeFormat == null) {
                popupOptions.customizeFormat = options.customizeFormat;
            }

            if (popupOptions.showStyle == null) {
                popupOptions.showStyle = options.showStyle;
            }

            if (popupOptions.showTemplate == null) {
                popupOptions.showTemplate = options.showTemplate;
            }

            if (popupOptions.showRawTemplate == null) {
                popupOptions.showRawTemplate = options.showRawTemplate;
            }
        }

        if (isForGauge) {
            // VR: for Gauge
            popupOptions.value = calcValue.current;
        } else {
            popupOptions.value = value;
        }

        popupOptions.isExpression = isExpression(popupOptions.value);

        props.glob.DialogActions.createNewDialog(
            'Format Value',
            handleOnChange,
            () => {},
            () => {},
            {      
                name: 'FormatChooser',          
                deny: false,
                confirmTitle: 'OK',
                componentOptions: popupOptions,
                Component: FormatChooserPopup
            }
        );
    };

    let valueToShow = '';

    // VR: for Gauge
    if (isForGauge) {
        const { precision, template } = (calcValue.current = getValueforGauge(props.values, value, props.options));

        valueToShow = calcValue.current.formatFront;

        if (precision != undefined && calcValue.current.type === ATTRIBUTE_TYPES.REAL) {
            valueToShow = valueToShow == null || valueToShow === 'default' ? '' : valueToShow;

            if (valueToShow.indexOf('currency') !== -1 && calcValue.current.currency != null) {
                valueToShow = valueToShow + '(' + calcValue.current.currency + ')';
            }

            if (precision != undefined && precision.toString() !== '') {
                valueToShow = valueToShow + ',' + precision;
            }

            valueToShow = template != null && template !== '' ? '[template]' : valueToShow;
        }
    } else {
        const { formatFront } = value;

        valueToShow = formatFront == 'custom' ? value.customFormat : formatFront;
        valueToShow = valueToShow == null || valueToShow === 'default' ? '' : valueToShow;

        //LL:  .type (=> interface IValueForGauge) is used not only in Gauge. Ex, in Charts FormatConfigField
        let attributeType = (value as IValueForGauge).type;

        if (attributeType == null) {
            attributeType = getDefaultAttributeType(
                value,
                props.glob,
                props.dataSourceID,
                props.getDefaultAttributeType
            );
        }

        if (attributeType == ATTRIBUTE_TYPES.BOOLEAN && formatFront === 'custom') {
            const _cust = parseBooleanCustomFormat(value.customFormat);

            valueToShow = _cust._true + '/' + _cust._false;
        } else {
            let showPrecision = false;

            if (
                attributeType === ATTRIBUTE_TYPES.REAL &&
                !isTimeIntervalValueFormat(formatFront) &&
                valueToShow !== ''
            ) {
                showPrecision = true;
            }

            if (valueToShow.indexOf('currency') !== -1 && value.currency != null) {
                valueToShow = valueToShow + '(' + value.currency + ')';
            }

            if (showPrecision && value.precision != undefined && value.precision.toString() !== '') {
                valueToShow = valueToShow + ',' + value.precision;
            }

            valueToShow = value.template != null && value.template !== '' ? '[template]' : valueToShow;
        }
    }

    if (props.showStyle) {
        const cellStyle = value.cellStyle;

        if (cellStyle != null) {
            if (
                cellStyle.backgroundColor != null ||
                cellStyle.backgroundColorSchema != null ||
                cellStyle.foregroundColor != null ||
                cellStyle.foregroundColorSchema != null ||
                cellStyle.background_mappingName != null ||
                cellStyle.background_mappingType != null ||
                cellStyle.foreground_mappingName != null ||
                cellStyle.foreground_mappingType != null ||
                cellStyle.fontItalic != null ||
                cellStyle.fontSize != null ||
                cellStyle.fontWeight != null ||
                cellStyle.fontFace != null ||
                !(cellStyle.alignment == null || cellStyle.alignment === 'left')
            ) {
                if (valueToShow.length > 0) {
                    valueToShow += '; ';
                }

                valueToShow += 'styling is set';
            }
        }
    }

    let title = 'Cell Format';

    return (
        <div className="format-chooser">
            <div className="content">
                <span
                    className="glyphicon glyphicon-cog"
                    style={{ cursor: 'pointer' }}
                    onClick={handleOnIconClick}
                ></span>
                <span className="value" title={title}>
                    {valueToShow}
                </span>
            </div>
        </div>
    );
}

export class FormatChooserField extends ParameterFieldBase<IParameterFieldBaseOptions> {
    public static EditComponent = FormatChooser;
    public static getDefaultValue() {
        return {};
    }

    constructor(glob: IGlob, title: string, validators?: any, defaultVal?: any, options?: IFormatChooserOptions) {
        if (defaultVal == null) {
            defaultVal = FormatChooserField.getDefaultValue();
        }

        super(glob, title, [], validators, defaultVal, options);
    }
}

export function formatIsCustomizable(attributeType: string) {
    return (
        DateTimeFunctions.isTypeOfInteger(attributeType) ||
        attributeType === ATTRIBUTE_TYPES.REAL ||
        attributeType === ATTRIBUTE_TYPES.BOOLEAN ||
        DateTimeFunctions.isTypeOfAnyDate(attributeType)
    );
}
